import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminApi } from '../services/admin';
import { useAsync } from '../hooks/useAsync';
import { EmptyState, Loading, Panel, Tag } from '../components/ui';
import { Pagination } from '../components/Pagination';
import { SearchInput } from '../components/SearchInput';
import { fmtDateTime } from '../lib/format';

export const RecipesPage: React.FC = () => {
  const [page, setPage] = useState(1);
  const [q, setQ] = useState('');

  useEffect(() => setPage(1), [q]);

  const { data, loading, error } = useAsync(
    () => adminApi.recipes({ page, pageSize: 20, q: q || undefined }),
    [page, q],
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <p className="max-w-xl text-sm text-cream-400">
          Every recipe saved across all users, with its owner and source.
        </p>
        <SearchInput value={q} onChange={setQ} placeholder="Search by title…" />
      </div>

      <Panel pad={false} className="overflow-hidden">
        {loading && !data ? (
          <Loading label="Loading recipes…" />
        ) : error ? (
          <EmptyState title="Couldn’t load recipes" hint={error} />
        ) : data && data.items.length ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-cream-600/15 text-left text-[0.68rem] uppercase tracking-[0.14em] text-cream-400">
                    <th className="px-5 py-3 font-semibold">Recipe</th>
                    <th className="px-5 py-3 font-semibold">Owner</th>
                    <th className="px-5 py-3 font-semibold">Source</th>
                    <th className="px-5 py-3 text-right font-semibold">Created</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cream-600/10">
                  {data.items.map((r) => (
                    <tr key={r.id} className="transition-colors hover:bg-saffron-500/[0.04]">
                      <td className="px-5 py-3">
                        <div className="font-medium text-cream-100">{r.title}</div>
                        {r.description && (
                          <div className="mt-0.5 max-w-md truncate text-xs text-cream-400">
                            {r.description}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        {r.userEmail ? (
                          <Link
                            to={`/users/${r.userId}`}
                            className="text-cream-300 hover:text-saffron-300"
                          >
                            {r.userEmail}
                          </Link>
                        ) : (
                          <span className="font-mono text-xs text-cream-400">{r.userId.slice(0, 8)}</span>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <Tag>{r.sourceType}</Tag>
                          {r.sourceUrl && (
                            <a
                              href={r.sourceUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs text-teal-400 hover:underline"
                            >
                              link ↗
                            </a>
                          )}
                        </div>
                      </td>
                      <td className="tnum px-5 py-3 text-right text-xs text-cream-400">
                        {fmtDateTime(r.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={data.page}
              totalPages={data.totalPages}
              total={data.total}
              onPage={setPage}
            />
          </>
        ) : (
          <EmptyState title="No recipes found" hint={q ? `No match for “${q}”` : undefined} />
        )}
      </Panel>
    </div>
  );
};
